/**
 * External dependencies
 */
import React from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { connect } from "react-redux";
import { map, noop, template } from "lodash";

/**
 * Internal dependencies
 */
import {
  getGrid,
  createWidgetConfig,
  getLayoutId,
} from "state/dashboard/selectors";
import { getPreference } from "state/preferences/selectors";
import ReportFactory from "lib/report-factory";
import { removeItemFromDashboard } from "state/dashboard/actions";
import { deleteReport } from "state/reports/actions";
import { startEditing as renameReport } from "state/ui/report-rename-form/actions";
import { useWindowSize } from "utils/use-window-size";

/**
 * Style dependencies
 */
import "./styles.scss";

const ResponsiveGridLayout = WidthProvider(Responsive);

const Grid = ({
  layout,
  widgetConfig,
  layoutId,
  rowHeight = 50,
  editorId,
  ...props
}) => {
  const {
    breakPoints: { isSmScreen },
  } = useWindowSize();
  const onLayoutChange = (currentLayout) => {
    // convert the layout back to original data
    let widgets = map(currentLayout, ({ i, w, h, x, y }) => {
      return {
        id: i,
        position: {
          w,
          h,
          x,
          y,
        },
      };
    });

    props?.onLayoutChange(layoutId, widgets);
  };

  /*
  LEAVE HERE SO CAN PRINT TO GET DEFAULT LAYOUT CONFIG
  console.log(
    "FINDME",
    JSON.stringify({
      name: "Default",
      items: widgetConfig.map((l) => {
        return {
          template: l.template,
          report: {
            name: l.report.name,
            chartType: l.report.chartType,
            template: l.report.template,
            config: {
              ...l.report.config,
            },
          },
          position: {
            ...l.position,
          },
        };
      }),
    })
  );

  */

  // add the actions to the widgets
  const config = map(widgetConfig, ({ report, ...c }) => ({
    ...c,
    actions: [
      {
        key: "rename",
        label: "Rename report",
        onClick: () => props?.renameReport(c.reportId),
      },
      {
        key: "remove",
        label: "Remove from deck",
        onClick: () => props?.removeItemFromDashboard(c.id),
      },
    ],
  }));

  const widgets = React.useMemo(() => {
    return ReportFactory.createAllReports(config);
  }, [layout.length, layoutId]);

  if (isSmScreen) {
    return (
      <div className="mobile-grid-container">
        {widgets.map((widget, index) => {
          return (
            <div key={index} className="mobile-grid-item">
              {widget}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <ResponsiveGridLayout
      key={layoutId}
      className="layout"
      cols={{ lg: 24, md: 24, sm: 24, xs: 24, xxs: 24 }}
      margin={[10, 10]}
      verticalCompact={true}
      horizontalCompact={true}
      rowHeight={rowHeight}
      layouts={{ lg: layout }}
      onLayoutChange={onLayoutChange}
      isDraggable={props.layoutEdit}
      isResizable={props.layoutEdit}
      draggableHandle=".dashboard-report--draggable"
    >
      {widgets}
    </ResponsiveGridLayout>
  );
};

export default connect(
  (state) => ({
    layout: getGrid(state),
    layoutId: getLayoutId(state),
    widgetConfig: createWidgetConfig(state),
    layoutEdit: getPreference(state, "layoutEdit"),
  }),
  {
    removeItemFromDashboard,
    deleteReport,
    renameReport,
  }
)(Grid);
